import {
  AppLayout,
  BreadcrumbGroup,
  ContentLayout,
  Flashbar,
  Icon,
  Spinner,
  Button,
  Popover,
  Link,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import {
  EventInput,
  EventStatus,
  ShiftInput,
  WaitlistEntry,
  WaitlistStatus,
} from '@amzn/red-velvet-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalizedText } from '../hooks/localizedText';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { DiscardChangesModal } from '../components/events/createEvent/components/discardChangesModal';
import { useWizardToolsPanel } from '../hooks/useWizardToolsPanel';
import { EventPreviewModal } from '../components/events/createEvent/components/EventPreviewModal';
import {
  getEventDetailsStep,
  getPublishStep,
  getShiftStep,
  getTeamAndPartnershipStep,
} from '../components/events/createEvent/steps';
import { CreateEventWizard } from '../components/events/wizard/createEventWizard';
import { useUpdateEvent } from '../hooks/events/updateEvent';
import { useUnpublishEvent } from '../hooks/events/unpublishEvent';
import { useEventPrePopulation } from '../hooks/events/useEventPrePopulation';
import { useWizardStrings } from '../hooks/localization/wizardStrings';
import { useErrorMessages } from '../hooks/localization/errorMessages';
import { useUnsavedChangesBrowserNavigationPrompt } from '../hooks/unsavedChanges';
import {
  validateEventDetails,
  validateShifts,
  validateTeamAndPartnership,
} from '../utils/eventValidation';
import { useFeatures, featureIsEnabled } from '../hooks/features';
import {
  COORDINATOR_SIGNUP_NOTIFICATIONS_INTERNAL,
  COORDINATOR_WAITLIST_OPT_OUT_TOGGLE_OFF,
  SHIFT_WAITLIST,
  NAVIGATION_REDESIGN,
} from '../utils/constants';
import { publishKatalMetric } from '../components/katalAnalytics';
import { useGetAllWaitlistsByEvent } from '../hooks/waitlist/useGetAllWaitlistsByEvent';
import { useBulkLeaveWaitlist } from '../hooks/waitlist/useBulkLeaveWaitlist';
import { groupEntriesByShift, applyWaitlistEnabledDefault } from '../utils/waitlistUtils';
import {
  EDIT_EVENT_STEP_PARAM,
  describeEditEventStepForMetric,
  isEditEventNotificationsDeepLink,
  readSyncErrorMessages,
  resolveEditEventStepIndex,
} from '../utils/eventUtils';
import { DisableWaitlistModal } from '../components/events/createEvent/components/DisableWaitlistModal';
import { AppOutletContext } from '../App';
import { urlWithPersistantParams } from '../components/SpaLink';

export function EditEvent() {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const wizardStrings = useWizardStrings('createEventWizard', namespaces);
  const { resolveError } = useErrorMessages();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const features = useFeatures();
  const coordinatorNotificationsEnabled = featureIsEnabled(
    COORDINATOR_SIGNUP_NOTIFICATIONS_INTERNAL,
    features,
    searchParams
  );
  const eventId = searchParams.get('id') ?? '';
  const urlFeaturesParam = searchParams.get('features');
  const viewEventUrl = `/view-event?id=${eventId}${urlFeaturesParam ? '&features=' + urlFeaturesParam : ''}`;

  const requestedStep = searchParams.get(EDIT_EVENT_STEP_PARAM);
  const requestedStepIndex = resolveEditEventStepIndex(requestedStep);
  const [page, setPage] = useState(requestedStepIndex ?? 0);
  const [highlightNotifications, setHighlightNotifications] = useState(() =>
    isEditEventNotificationsDeepLink(requestedStep)
  );

  // Back should leave the page, not walk the coordinator back through wizard steps.
  const goToStep = (stepIndex: number) => {
    setPage(stepIndex);
    setSearchParams(
      (current) => {
        const nextParams = new URLSearchParams(current);
        nextParams.set(EDIT_EVENT_STEP_PARAM, String(stepIndex + 1));
        return nextParams;
      },
      { replace: true }
    );
  };
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasPrePopulated, setHasPrePopulated] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [unpublishError, setUnpublishError] = useState<string | null>(null);
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [showPublishError, setShowPublishError] = useState(false);
  const [discardModalVisible, setDiscardModalVisible] = useState(false);
  // Set by the navigation guard when a SPA navigation (side nav, breadcrumbs, top-nav logo) is
  // blocked mid-edit. When present it overrides the wizard Cancel button's viewEventUrl target so
  // the discard modal returns the user to wherever they were headed.
  const [pendingNavHref, setPendingNavHref] = useState<string | null>(null);

  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);
  const outletContext = useOutletContext<AppOutletContext | null>();
  // Pass outletContext as controller so the hook delegates open/close to AppLayout.
  // This prevents the reopen-loop bug where child useEffects fought with AppLayout's state.
  const wizardTools = useWizardToolsPanel(isNewNav ? outletContext : null);

  // Clean up tools panel on unmount (new nav mode)
  useEffect(() => {
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setTools(null);
        outletContext.setToolsOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav]);

  const shiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  // Edit flow only: load the event's waitlist so we can gate per-shift toggle-off on active entries.
  // Passing '' when the feature is off disables the query. Failure is non-blocking (see toggle).
  const waitlistQuery = useGetAllWaitlistsByEvent(shiftWaitlistEnabled ? eventId : '');
  const activeEntriesByShift = useMemo(() => {
    const map = new Map<string, WaitlistEntry[]>();
    for (const entry of waitlistQuery.data?.waitlistEntries ?? []) {
      if (entry.status === WaitlistStatus.ACTIVE) {
        const list = map.get(entry.shiftId) ?? [];
        list.push(entry);
        map.set(entry.shiftId, list);
      }
    }
    return map;
  }, [waitlistQuery.data]);
  const bulkLeaveMutation = useBulkLeaveWaitlist();
  // Keyed by shiftId (not array index) so removing/reordering shifts in the wizard can't
  // misapply the modal target to the wrong shift (CR-290074141).
  const [disableWaitlistShiftId, setDisableWaitlistShiftId] = useState<string | null>(null);

  useUnsavedChangesBrowserNavigationPrompt(hasUnsavedChanges);

  // Cloudscape unsaved-changes pattern: block SPA navigation (side nav, breadcrumbs, top-nav logo)
  // while the edit wizard has unsaved changes. useUnsavedChangesBrowserNavigationPrompt only covers
  // browser-level navigation; SPA clicks bypass it, so a side-nav click mid-edit would silently
  // discard changes (CR-291797692 rev 8). The guard captures the target href, shows the existing
  // discard modal, and blocks; confirming navigates to the captured href.
  const navigationGuard = useCallback(
    (href: string) => {
      if (hasUnsavedChanges) {
        setPendingNavHref(href);
        setDiscardModalVisible(true);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges]
  );

  useEffect(() => {
    if (isNewNav && outletContext) {
      outletContext.setNavigationGuard(navigationGuard);
    }
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setNavigationGuard(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav, navigationGuard]);

  useEffect(() => {
    if (eventId && requestedStep && requestedStepIndex === undefined) {
      publishKatalMetric(
        'editEvent_unknownDeepLinkStep',
        describeEditEventStepForMetric(requestedStep)
      );
    }
  }, [eventId, requestedStep, requestedStepIndex]);

  const {
    eventInput: prePopulatedEvent,
    shiftInputs,
    shiftIdsWithSignups,
    totalSignupCount,
    hasPastShiftsWithAttendance,
    eventStatus,
    earliestShiftIdWithSignups,
    ownerAlias,
    isLoading,
    error: loadError,
  } = useEventPrePopulation(eventId || null);

  const updateEventMutation = useUpdateEvent();
  const unpublishEventMutation = useUnpublishEvent();

  const initialEventDetailsStepField: EventInput = {
    title: '',
    description: '',
    shortDescription: '',
    focusArea: '',
    primaryActivity: '',
  };
  const [eventDetailsStepField, setEventDetailsStepField] = useState<EventInput>(
    initialEventDetailsStepField
  );
  const [shiftStepField, setShiftStepField] = useState<ShiftInput[]>([]);

  // Pre-populate form when data loads — must wait for both queries to resolve
  useEffect(() => {
    if (prePopulatedEvent && !isLoading) {
      setEventDetailsStepField(prePopulatedEvent);
      setShiftStepField(shiftInputs);
      setHasPrePopulated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shiftInputs is a new ref each render; prePopulatedEvent + isLoading gate this effect
  }, [prePopulatedEvent, isLoading]);

  const validateAllSteps = (): Record<string, string> => {
    const allErrors = {
      ...validateEventDetails(eventDetailsStepField, t),
      ...validateShifts(shiftStepField, t),
      ...validateTeamAndPartnership(eventDetailsStepField, t, {
        coordinatorNotificationsEnabled,
      }),
    };
    setErrors(allErrors);
    return allErrors;
  };

  const updateEventFieldHandler = (args: EventInput) => {
    setEventDetailsStepField(args);
    setHasUnsavedChanges(true);
    setErrors((prev) => {
      const remaining = validateEventDetails(args, t);
      const teamRemaining = validateTeamAndPartnership(args, t, {
        coordinatorNotificationsEnabled,
      });
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (
          !key.startsWith('shift_') &&
          key !== 'publishError' &&
          !remaining[key] &&
          !teamRemaining[key]
        ) {
          delete next[key];
        }
      }
      return next;
    });
  };

  const disableTargetShift =
    disableWaitlistShiftId != null
      ? shiftStepField.find((s) => s.shiftId === disableWaitlistShiftId)
      : undefined;
  const disableTargetEntries = disableTargetShift?.shiftId
    ? (activeEntriesByShift.get(disableTargetShift.shiftId) ?? [])
    : [];

  const closeDisableWaitlistModal = () => setDisableWaitlistShiftId(null);

  // Confirming only flips the flag to off in local state — consistent with every other shift edit,
  // nothing is persisted until the coordinator saves. The active waitlist entries are removed
  // (best-effort) at final submit, not here (CR-290074141).
  const handleConfirmDisableWaitlist = () => {
    const shiftId = disableWaitlistShiftId;
    if (shiftId == null) return;
    publishKatalMetric(COORDINATOR_WAITLIST_OPT_OUT_TOGGLE_OFF, '');
    setShiftStepField((prev) =>
      prev.map((s) => (s.shiftId === shiftId ? { ...s, waitlistEnabled: false } : s))
    );
    setHasUnsavedChanges(true);
    setDisableWaitlistShiftId(null);
  };

  const hasValidationErrors =
    showPublishError && Object.keys(errors).some((key) => key !== 'publishError');

  const steps = [
    getEventDetailsStep(
      t('eventDetailsStep.eventDetails'),
      t('eventDetailsStep.eventDetailsSubText'),
      eventDetailsStepField,
      updateEventFieldHandler,
      'editEvent',
      errors,
      wizardTools.openGrantPanel,
      wizardTools.openCommunityOrganizationPanel,
      eventId || undefined
    ),
    getShiftStep(
      t('shiftStep.stepTitle'),
      t('shiftStep.headerDescription'),
      shiftStepField,
      (args) => {
        setShiftStepField(args);
        setHasUnsavedChanges(true);
      },
      eventDetailsStepField,
      updateEventFieldHandler,
      'editEvent',
      errors,
      shiftIdsWithSignups,
      wizardTools.openShiftTypePanel,
      {
        onInfoClick: wizardTools.openWaitlistPanel,
        loading: shiftWaitlistEnabled && waitlistQuery.isLoading,
        error: shiftWaitlistEnabled && waitlistQuery.isError,
        activeCountByShiftId: (shiftId: string) => activeEntriesByShift.get(shiftId)?.length ?? 0,
        onAttemptDisable: (shiftId: string) => setDisableWaitlistShiftId(shiftId),
      }
    ),
    getTeamAndPartnershipStep(
      t('teamPartnershipStep.stepTitle'),
      t('teamPartnershipStep.stepDescription'),
      eventDetailsStepField,
      updateEventFieldHandler,
      'editEvent',
      errors,
      ownerAlias,
      eventStatus,
      {
        active: highlightNotifications,
        onShown: () => setHighlightNotifications(false),
      },
      wizardTools.openCoordinatorNotificationPanel
    ),
    getPublishStep(
      t('reviewPublishStep.stepTitle'),
      t('reviewPublishStep.preview'),
      () => {
        publishKatalMetric('editEvent_previewModalOpened', eventId);
        setIsPreviewModalVisible(true);
      },
      {
        currentEventFields: eventDetailsStepField,
        currentShiftFields: shiftStepField,
        updateShiftFieldHandler: (args: ShiftInput[]) => {
          setShiftStepField(args);
          setHasUnsavedChanges(true);
        },
        updateEventFieldHandler,
        setActiveStepIndex: (index: number) => {
          setSubmitError(null);
          setUnpublishError(null);
          goToStep(index);
        },
        errors,
        publicationStatus:
          eventStatus === EventStatus.UNPUBLISHED
            ? 'unpublished'
            : hasUnsavedChanges
              ? 'pending'
              : 'published',
        totalSignupCount,
        submitError,
        onDismissError: () => setSubmitError(null),
        metricPrefix: 'editEvent',
        ownerAlias,
        eventStatus,
        onDismissPublishError: () =>
          setErrors((prev) => {
            const { publishError, ...rest } = prev;
            return rest;
          }),
      },
      hasValidationErrors ? t('error.resolveRequiredFields') : undefined
    ),
  ];

  if (!eventId) {
    const errorContent = (
      <ContentLayout>
        <Flashbar items={[{ type: 'error', content: t('error.eventIdRequired') }]} />
      </ContentLayout>
    );
    if (isNewNav) return errorContent;
    return (
      <AppLayout
        footerSelector=".footer"
        navigationHide={true}
        toolsHide={true}
        content={errorContent}
        contentType="wizard"
      />
    );
  }

  const isReady = !isLoading && (hasPrePopulated || !!loadError);
  const earlyContent = loadError ? (
    <Flashbar items={[{ type: 'error', content: resolveError(loadError) }]} />
  ) : !isReady ? (
    <Spinner size="large" />
  ) : null;

  // Shared wizard element used by both new-nav and legacy render paths. Extracting
  // this into a single variable ensures both layouts render the exact same wizard —
  // including secondaryActions ("Move to drafts" or its disabled/popover variants),
  // the waitlist cleanup on submit, and the applyWaitlistEnabledDefault wrapper —
  // which the new-nav branch was previously silently omitting (CR-291797692 ITL Hydra
  // failures).
  const wizardMarkup = (
    <CreateEventWizard
      metricPrefix="editEvent"
      eventId={eventId}
      submitMetricName={
        eventStatus === EventStatus.UNPUBLISHED
          ? 'editEvent_submitAndRepublish'
          : 'editEvent_submit'
      }
      i18nStrings={{
        ...wizardStrings,
        submitButton:
          eventStatus === EventStatus.UNPUBLISHED
            ? t('createEventWizard.editAndPublishButton')
            : t('createEventWizard.submitChangesButton'),
        nextButton: t([`createEventWizard.nextButton`], {
          title: steps[page + 1] ? steps[page + 1].title : t([`wizard.nextButton`]),
        }),
      }}
      steps={steps}
      isLoadingNextStep={updateEventMutation.isPending || unpublishEventMutation.isPending}
      // "Move to drafts" button: hidden when already UNPUBLISHED (no-op) or when
      // past shifts have attendance (no path back to draft). Disabled with popover
      // when signups exist. Enabled otherwise.
      secondaryActions={
        eventStatus === EventStatus.UNPUBLISHED ||
        hasPastShiftsWithAttendance ? undefined : totalSignupCount > 0 ? (
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Button disabled>{t('createEventWizard.moveToDrafts')}</Button>
            <Popover
              header={t('createEventWizard.moveToDrafts')}
              content={
                <SpaceBetween size="xs">
                  <span>
                    {t('createEventWizard.moveToDraftsDisabledReason', {
                      count: totalSignupCount,
                    })}
                  </span>
                  <Link
                    href={`/manage-signups?eventId=${eventId}&shiftId=${earliestShiftIdWithSignups}`}
                    target="_blank"
                  >
                    {t('createEventWizard.moveToDraftsManageSignupsLink')}
                  </Link>
                </SpaceBetween>
              }
              triggerType="custom"
              size="large"
            >
              <Icon name="status-info" />
            </Popover>
          </SpaceBetween>
        ) : (
          <Button
            loading={unpublishEventMutation.isPending}
            disabled={updateEventMutation.isPending}
            onClick={() => {
              setUnpublishError(null);
              unpublishEventMutation.mutate(
                {
                  eventId,
                  syncBenevity: searchParams.get('syncBenevity') === 'true' || undefined,
                },
                {
                  onSuccess: () => {
                    publishKatalMetric('editEvent_unpublishSuccess', eventId);
                    setHasUnsavedChanges(false);
                    navigate(viewEventUrl);
                  },
                  onError: (err) => {
                    publishKatalMetric('editEvent_unpublishFailure', eventId);
                    setUnpublishError(resolveError(err));
                  },
                }
              );
            }}
          >
            {t('createEventWizard.moveToDrafts')}
          </Button>
        )
      }
      onCancel={() => {
        if (hasUnsavedChanges) {
          setDiscardModalVisible(true);
        } else {
          navigate(viewEventUrl);
        }
      }}
      onNavigate={({ detail }) => {
        setShowPublishError(false);
        wizardTools.close();
        goToStep(detail.requestedStepIndex);
      }}
      onSubmit={() => {
        setSubmitError(null);
        const allErrors = validateAllSteps();
        if (Object.keys(allErrors).length > 0) {
          setShowPublishError(true);
          publishKatalMetric('editEvent_validationFailure', Object.keys(allErrors).join(','));
          setTimeout(() => {
            const firstError = document.querySelector('[class*="awsui_error__message"]');
            if (firstError) {
              firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 0);
          return;
        }
        // Waitlist removal is deferred from the toggle to submit (best-effort): gather
        // active entries for shifts the coordinator disabled this session. The disabled
        // flag is the source of truth (persisted by updateEvent below) — if removal fails
        // we still navigate; coordinators can finish cleanup from the Manage Waitlist page
        // (CR-290074141).
        const entriesToRemove = shiftStepField.flatMap((s) =>
          s.shiftId && s.waitlistEnabled === false
            ? (activeEntriesByShift.get(s.shiftId) ?? [])
            : []
        );
        updateEventMutation.mutate(
          {
            eventId,
            event: eventDetailsStepField,
            shifts: applyWaitlistEnabledDefault(shiftStepField, shiftWaitlistEnabled),
            syncBenevity: searchParams.get('syncBenevity') === 'true' || undefined,
          },
          {
            onSuccess: (response) => {
              // Always update shifts with backend-assigned IDs to prevent duplicates on retry
              setShiftStepField(response.shifts);

              const syncErrorMessages = readSyncErrorMessages(response, resolveError);
              if (syncErrorMessages.length) {
                publishKatalMetric('editEvent_partialFailure', syncErrorMessages.length.toString());
                setSubmitError(
                  t('error.syncErrorsPartialFailure', {
                    errors: syncErrorMessages.join('; '),
                  })
                );
                return;
              }

              publishKatalMetric('editEvent_updateSuccess', eventId);
              const finishSubmit = () => {
                setHasUnsavedChanges(false);
                navigate(viewEventUrl);
              };
              if (entriesToRemove.length > 0) {
                // Best-effort cleanup: navigate regardless of the removal outcome.
                bulkLeaveMutation.mutate(
                  { entries: groupEntriesByShift(entriesToRemove) },
                  {
                    onError: () => publishKatalMetric('editEvent_waitlistRemoveFailure', eventId),
                    onSettled: finishSubmit,
                  }
                );
              } else {
                finishSubmit();
              }
            },
            onError: (err) => {
              publishKatalMetric('editEvent_updateFailure', eventId);
              setSubmitError(resolveError(err));
            },
          }
        );
      }}
      activeStepIndex={page}
      stepError={
        unpublishError ? (
          <Flashbar
            items={[
              {
                type: 'error',
                content: unpublishError,
                dismissible: true,
                onDismiss: () => setUnpublishError(null),
              },
            ]}
          />
        ) : undefined
      }
    />
  );

  const wizardBody = <ContentLayout>{earlyContent ?? wizardMarkup}</ContentLayout>;

  const modals = (
    <>
      <DiscardChangesModal
        visible={discardModalVisible}
        onDismiss={() => {
          setDiscardModalVisible(false);
          setPendingNavHref(null);
        }}
        onConfirm={() => {
          publishKatalMetric('editEvent_discardChanges', eventId);
          setDiscardModalVisible(false);
          setHasUnsavedChanges(false);
          // When the guard blocked a SPA navigation, return the user to that destination;
          // otherwise (wizard Cancel button) fall back to the event view page.
          // pendingNavHref is the raw href from the nav guard and carries no query string, so
          // route it through the persistent-param helper or features/timezone are dropped and
          // the user lands back in the legacy layout. viewEventUrl already embeds features.
          navigate(
            pendingNavHref ? urlWithPersistantParams(searchParams, pendingNavHref) : viewEventUrl
          );
          setPendingNavHref(null);
        }}
      />
      <EventPreviewModal
        currentEventFields={eventDetailsStepField}
        currentShiftFields={shiftStepField}
        isVisible={isPreviewModalVisible}
        onDismiss={() => setIsPreviewModalVisible(false)}
        eventId={eventId}
      />
      <DisableWaitlistModal
        visible={disableWaitlistShiftId != null}
        activeWaitlistsCount={disableTargetEntries.length}
        loading={false}
        onConfirm={handleConfirmDisableWaitlist}
        onDismiss={closeDisableWaitlistModal}
      />
    </>
  );

  // New navigation: parent AppLayout in App.tsx owns the chrome (side nav, breadcrumbs,
  // tools panel). We render content only; tools are synced to the parent via
  // outletContext in the useEffect above.
  if (isNewNav) {
    return (
      <>
        {wizardBody}
        {modals}
      </>
    );
  }

  // Legacy: page owns its own AppLayout wrapper.
  return (
    <>
      <AppLayout
        footerSelector=".footer"
        navigationHide={true}
        toolsHide={wizardTools.toolsHide}
        toolsOpen={wizardTools.toolsOpen}
        onToolsChange={wizardTools.onToolsChange}
        tools={wizardTools.tools}
        breadcrumbs={
          <BreadcrumbGroup
            items={[
              {
                text: t('breadcrumbs.myActivity'),
                href: `/my-activity${urlFeaturesParam ? '?features=' + urlFeaturesParam : ''}`,
              },
              {
                text: t('breadcrumbs.coordinatorActivity'),
                href: `/coordinator-activity${urlFeaturesParam ? '?features=' + urlFeaturesParam : ''}`,
              },
              { text: t('breadcrumbs.editEvent'), href: '#' },
            ]}
          />
        }
        content={wizardBody}
        contentType="wizard"
      />
      {modals}
    </>
  );
}
